// Base Libs
import {
    AnswerOption,
    AnswerOptionBuiltInTypeEnum,
    EvaluationQuestion,
    EvaluationQuestionScore,
    EvaluationFormEvaluationFormTypeEnum,
} from "@genesys/cloud-frontend/models";
import { v4 as uuidv4 } from "uuid";

// Base commons
import { RootState } from "app/store";
import { DefaultLimits } from "constants/DefaultLimits";

// Feature slices
import { TemplateQuestion } from "./qualityFormTypes";
import { QualityFormState } from "./qualityFormStates";

// Quality form commons
import { BaseQuestionType } from "../common/BaseQuestionType";

// Form Editor
import {
    EVALUATION_YES_NO_QUESTION,
    SubQuestionType,
    QuestionInfoType,
} from "../formEditor/common/QuestionType";
import NPSScoreCategories from "../formEditor/common/NPSScoreCategories";

// Locales
import { Locale, renderString } from "i18n";
import { selectFeatureToggle } from "features/featureToggles/featureToggleSlice";
import FeatureToggle from "enums/FeatureToggle";

// Private functions
const selectMCQAnswerOption = (state: RootState, answerOptionId: string) => {
    const data = state.qualityForm.data;
    return data.answerOptions?.[answerOptionId];
};

// Public functions
export const isUndefinedOrNull = (value) => {
    return value === undefined || value === null;
};

export const getAnswerOptionsTemplate = (
    subQuestionType: SubQuestionType,
    locale: Locale
) => {
    if (subQuestionType === SubQuestionType.YES_OR_NO) {
        return [
            {
                text: renderString(
                    locale,
                    "formEditor.QuestionGroupList.addQuestion.answerOptions.yes"
                ),
                value: 1,
            },
            {
                text: renderString(
                    locale,
                    "formEditor.QuestionGroupList.addQuestion.answerOptions.no"
                ),
                value: 0,
            },
        ];
    } else if (subQuestionType === SubQuestionType.MULTIPLE_CHOICE) {
        return Array.from({ length: 3 }, () => {
            return { text: "", value: 0 };
        });
    } else if (subQuestionType === SubQuestionType.RANGE) {
        return Array.from({ length: 5 }, (_, i) => {
            return { text: (i + 1).toString(), value: i + 1 };
        });
    } else {
        return [];
    }
};

export const getBaseQuestionTemplate = (
    questionType: QuestionInfoType
): any => {
    return {
        naEnabled: false,
        text: "",
        isKill: false,
        commentsRequired: false,
        answerOptions: [],
        helpText: "",
        isCritical: false,
        type: questionType.questionType,
        maxResponseCharacters: DefaultLimits.initMaxResponseCharacters,
        explanationPrompt: "",
        contextId: undefined,
        multipleSelectOptionQuestions: [],
    };
};

export const getMultipleSelectOptionQuestion = (): EvaluationQuestion => {
    // Just pass yes/no so the type is multiple choice.
    const mSelectQ = getBaseQuestionTemplate(EVALUATION_YES_NO_QUESTION);
    mSelectQ.answerOptions = [
        {
            id: uuidv4(),
            builtInType: "Unselected",
            value: 0,
        },
        {
            id: uuidv4(),
            builtInType: "Selected",
            value: 0,
            assistanceConditions: [],
        },
    ];
    return mSelectQ;
};

export const getMultipleSelectOptionQuestions = (): EvaluationQuestion[] => {
    const result: EvaluationQuestion[] = Array.from(
        { length: DefaultLimits.minMultipleSelectQuestionOptionCount },
        () => getMultipleSelectOptionQuestion()
    );
    return result;
};

export const getQuestionTemplate = (
    questionType: QuestionInfoType,
    locale: Locale,
    isMultipleSelectFTEnabled = false
): TemplateQuestion => {
    const result: TemplateQuestion = getBaseQuestionTemplate(
        questionType
    ) as TemplateQuestion;
    result.answerOptions = getAnswerOptionsTemplate(
        questionType.subQuestionType,
        locale
    );
    if (
        isMultipleSelectFTEnabled &&
        questionType.subQuestionType === SubQuestionType.MULTIPLE_SELECT
    ) {
        result.multipleSelectOptionQuestions =
            getMultipleSelectOptionQuestions();
    }
    return result;
};

export const selectForm = (state: RootState) => state.qualityForm.data.form;
export const selectFormType = (state: RootState) =>
    state.qualityForm.data.formType;

/**
 * Function that helps logic gets to a question object.
 * This function is used agnostically of question type, just return a question and that's it.
 */
export const selectQuestion = (state: RootState, questionId: string) => {
    const questions = state.qualityForm.data.questions;
    return questions?.[questionId];
};

/**
 * Function that helps logic gets to a scorable question.
 * When isMultipleSelect is true, the question id represents one of the MSQ answers which is literally a YES/NO MCQ question.
 * When isMultipleSelect is false, the question id represents a regular MCQ question.
 */
export const selectScorableQuestion = (
    state: RootState,
    questionId: string,
    isMultipleSelect?: boolean
) => {
    const data = state.qualityForm.data;
    let questions;
    if (isMultipleSelect) {
        questions = data.multipleSelectOptionQuestions;
    } else {
        questions = data.questions;
    }
    return questions?.[questionId];
};

export const selectAnswerOptionIdList = (
    state: RootState,
    questionId: string,
    isMultipleSelect?: boolean
): Readonly<Array<string> | undefined> => {
    const question = selectQuestion(state, questionId);
    if (isMultipleSelect) {
        return question?.multipleSelectOptionQuestions as Array<string>;
    } else {
        return question?.answerOptions as Array<string>;
    }
};

/**
 * This function gives the immutable-signified answer option for both MCQ and MSQ.
 * @param state
 * @param answerOptionId
 * @param isMultipleSelect
 * @returns
 */
export const selectAnswerOption = (
    state: RootState,
    answerOptionId: string,
    isMultipleSelect?: boolean
): Readonly<AnswerOption | undefined> => {
    const data = state.qualityForm.data;
    let answerOption: AnswerOption | undefined;
    if (isMultipleSelect) {
        // *** Note this only returns AnswerOption proxy object as multiple selection has not actual AnswerOption
        // like any other regular question
        const mSelectOptionsQ =
            data.multipleSelectOptionQuestions?.[answerOptionId];
        if (mSelectOptionsQ) {
            let value;
            let assistanceConditions;
            const answerOptions = mSelectOptionsQ.answerOptions;
            (answerOptions || []).some((option) => {
                if (option.builtInType === "Selected") {
                    value = option.value;
                    assistanceConditions = option.assistanceConditions;
                    return true;
                }
                return false;
            });
            answerOption = {
                id: answerOptionId,
                contextId: mSelectOptionsQ?.contextId,
                text: mSelectOptionsQ?.text,
                value,
                assistanceConditions,
            };
        }
    } else {
        answerOption = selectMCQAnswerOption(state, answerOptionId);
    }
    return answerOption;
};

/**
 * This function gives the mutable answer option for both MCQ and MSQ.
 * @param state
 * @param answerOptionId
 * @param isMultipleSelect
 * @returns
 */
export const selectAnswerOptionMutable = (
    // ***NOTE THIS IS QualityFormState NOT RootState***
    state: QualityFormState,
    answerOptionId: string,
    isMultipleSelect?: boolean,
    isSelected = true
) => {
    if (!isMultipleSelect) {
        return selectMCQAnswerOption(
            { qualityForm: state } as RootState,
            answerOptionId
        );
    }
    const data = state.data;
    let answerOption: AnswerOption | undefined;
    const mSelectOptionsQ =
        data.multipleSelectOptionQuestions?.[answerOptionId];
    if (mSelectOptionsQ) {
        const answerOptions = mSelectOptionsQ.answerOptions;
        (answerOptions || []).some((option) => {
            if (
                isSelected &&
                option.builtInType === AnswerOptionBuiltInTypeEnum.Selected
            ) {
                answerOption = option;
                return true;
            } else if (
                !isSelected &&
                option.builtInType === AnswerOptionBuiltInTypeEnum.Unselected
            ) {
                answerOption = option;
                return true;
            }
            return false;
        });
    }
    return answerOption;
};

export const selectAnswerOptionsForQuestion = (
    state: RootState,
    questionId: string
) => {
    const question = selectQuestion(state, questionId);
    if (question?.type === BaseQuestionType.NpsQuestion) {
        return Object.keys(NPSScoreCategories);
    }
    if (question?.type === BaseQuestionType.MultipleChoiceQuestion) {
        return question.answerOptions;
    }
    if (question?.type === BaseQuestionType.MultipleSelectQuestion) {
        return question.multipleSelectOptionQuestions;
    }
};

export const selectAssistanceConditions = (
    state: RootState,
    answerId: string,
    isMultipleSelect?: boolean
) => {
    const answerOption = selectAnswerOption(state, answerId, isMultipleSelect);
    return answerOption?.assistanceConditions;
};

export const selectEvaluationFormType = (
    state: RootState
): EvaluationFormEvaluationFormTypeEnum | undefined => {
    const evaluationFormType = state.qualityForm.data.form?.evaluationFormType;
    return evaluationFormType ?? undefined;
};

export const selectIsAutomatedEvaluationForm = (state: RootState): boolean => {
    return (
        selectEvaluationFormType(state) ===
        EvaluationFormEvaluationFormTypeEnum.Automated
    );
};

/**
 * Return the object mapping of each answer option ID with corresponding Selected and Unselected option IDs for the particular question
 * @param state
 * @param questionId
 */
export const getMultipleSelectQuestionOptionsMapping = (
    state: RootState,
    questionId: string
) => {
    const question = selectQuestion(state, questionId);
    const idMappings = {};
    // iterate question?.multipleSelectQuestionOptions
    // and create a mapping of each answer option ID with corresponding Selected and Unselected option IDs
    if (question && question.type === BaseQuestionType.MultipleSelectQuestion) {
        question?.multipleSelectOptionQuestions?.forEach((answerOption) => {
            const selectedOptionId = selectAnswerOptionMutable(
                state.qualityForm,
                answerOption,
                true,
                true
            )?.id;
            const unselectedOptionId = selectAnswerOptionMutable(
                state.qualityForm,
                answerOption,
                true,
                false
            )?.id;
            idMappings[answerOption] = {
                Selected: selectedOptionId,
                Unselected: unselectedOptionId,
            };
        });
    }
    return idMappings;
};

export const getQuestionTotalScore = (
    state: RootState,
    questionId: string,
    isMultipleSelect?: boolean
): number => {
    // go through all the multiple selection option item IDs and sum up their value
    let totalScore = 0;
    const answerOptionIdList = selectAnswerOptionIdList(
        state,
        questionId,
        isMultipleSelect
    );
    answerOptionIdList?.forEach((answerOptionId) => {
        const answerOption = selectAnswerOption(
            state,
            answerOptionId,
            isMultipleSelect
        );
        if (answerOption && answerOption.value) {
            totalScore += answerOption.value;
        }
    });
    return totalScore;
};

export const traverseMSQOptionIds = (
    state: RootState,
    questionId: string,
    callback: (
        question: any,
        optionScoreMap: Map<string, EvaluationQuestionScore>
    ) => any
): any => {
    const question = state.qualityForm.data.questions?.[questionId];
    const questionScore = state.scorable.data.questionScores[questionId];
    // Early return if not a multiple select question
    if (
        !question ||
        question.type !== BaseQuestionType.MultipleSelectQuestion
    ) {
        return undefined;
    }

    // Create a map for faster lookups, only including scores with defined questionId
    const optionScoreMap = new Map(
        questionScore?.multipleSelectQuestionOptionScores
            ?.filter((score) => !isUndefinedOrNull(score.questionId))
            .map((score) => [score.questionId, score])
    ) as Map<string, EvaluationQuestionScore>;

    // callback
    return callback(question, optionScoreMap);
};

/**
 * Return the list of answer IDs that are currently selected in the Multiple Select Question
 * @param state
 * @param questionId
 * @returns
 */
export const selectCurrentAnswerIdsForMSQ = (
    state: RootState,
    questionId: string
) => {
    return traverseMSQOptionIds(
        state,
        questionId,
        (question, optionScoreMap) => {
            // Filter and map in one pass
            return (question.multipleSelectOptionQuestions || []).filter(
                (optionId) => {
                    const optionScore = optionScoreMap.get(optionId);
                    if (!optionScore) return false;
                    const expectedAnswerId = selectAnswerOptionMutable(
                        state.qualityForm,
                        optionId,
                        true,
                        true
                    )?.id;
                    return (
                        expectedAnswerId &&
                        expectedAnswerId === optionScore.answerId
                    );
                }
            );
        }
    );
};

/**
 *
 * @param state
 * @param questionId
 * @returns the object where key is answer option main ID and the value is the assisted answer ID
 */
export const selectAssistedAnswerIdsForMSQ = (
    state: RootState,
    questionId: string
) => {
    return traverseMSQOptionIds(
        state,
        questionId,
        (question, optionScoreMap) => {
            // Reduce to create the result object in one pass
            return (question.multipleSelectOptionQuestions || []).reduce(
                (result, optionId) => {
                    const optionScore = optionScoreMap.get(optionId);
                    if (optionScore?.assistedAnswerId) {
                        result[optionId] = optionScore.assistedAnswerId;
                    }
                    return result;
                },
                {}
            );
        }
    );
};

export const isVisibilityConditionEligibleQuestionType = (
    questionType: any,
    isMultipleSelectFTEnabled = false
): boolean => {
    let result =
        questionType === BaseQuestionType.MultipleChoiceQuestion ||
        questionType === BaseQuestionType.NpsQuestion;
    if (isMultipleSelectFTEnabled) {
        result =
            result || questionType === BaseQuestionType.MultipleSelectQuestion;
    }
    return result;
};

export const selectMaxPermissibleQuestionsWithAiScoring = (
    state: RootState
) => {
    const isMaxAiScoringQuestionsPerFormV2Enabled = selectFeatureToggle(
        state,
        FeatureToggle.AI_SCORING_QUESTION_LIMIT_20
    );

    const maxLimit = isMaxAiScoringQuestionsPerFormV2Enabled
        ? DefaultLimits.maxPermissibleQuestionsWithAiScoringV2
        : DefaultLimits.maxPermissibleQuestionsWithAiScoring;

    return maxLimit;
};

export const selectSelectedAutomatedScoringType = (
    state: RootState,
    questionId: string
) => {
    const question = selectQuestion(state, questionId);
    return question?.automatedScoringType;
};

/**
 * Determines if automated evaluation features are enabled.
 * Requires both AUTOMATED_EVALUATION_COMPLETION and EVALUATION_FORM_DEFAULT_ANSWERING
 * feature toggles to be enabled.
 */
export const selectAutomatedEvaluationFeatureEnabled = (state: RootState) =>
    selectFeatureToggle(state, FeatureToggle.AUTOMATED_EVALUATION_COMPLETION) &&
    selectFeatureToggle(state, FeatureToggle.EVALUATION_FORM_DEFAULT_ANSWERING);

/**
 * Selects the ID of the selected option for a Multiple Select Answer (MSQ)
 * @param state - The root state of the application
 * @param answerOptionId - The ID of the answer option to check
 * @returns The ID of the selected option if found, undefined otherwise
 */
export const selectSelectedOptionIdForMSQ = (
    state: RootState,
    answerOptionId: string
) => {
    const data = state.qualityForm.data;

    const mSelectOptionsQ =
        data.multipleSelectOptionQuestions?.[answerOptionId];

    if (mSelectOptionsQ) {
        let selectedId;
        const answerOptions = mSelectOptionsQ.answerOptions;
        (answerOptions || []).some((option) => {
            if (option.builtInType === "Selected") {
                selectedId = option.id;
                return true;
            }
            return false;
        });
        return selectedId;
    }
};

/**
 * Checks if an answer option is the default answer for a question
 * @param state - The root state of the application
 * @param questionId - The ID of the question to check
 * @param optionId - The ID of the answer option to check
 * @param isMultipleSelect - Whether the question is a multiple select question
 * @returns Boolean indicating if the option is the default answer
 */
export const selectIsAnswerDefaultAnswer = (
    state: RootState,
    questionId: string,
    optionId: string,
    isMultipleSelect: boolean
) => {
    if (isMultipleSelect) {
        const multipleSelectOptionQuestion =
            state.qualityForm.data.multipleSelectOptionQuestions?.[optionId];

        const selectedAnswerOption = selectSelectedOptionIdForMSQ(
            state,
            optionId
        );

        return (
            !!multipleSelectOptionQuestion?.defaultAnswer?.id &&
            multipleSelectOptionQuestion?.defaultAnswer.id ===
                selectedAnswerOption
        );
    } else {
        const question = selectQuestion(state, questionId);
        return (
            !!question?.defaultAnswer?.id &&
            question?.defaultAnswer.id === optionId
        );
    }
};

/**
 * Checks if a default answer is selected for a given question
 * @param state - The root state of the application
 * @param questionId - The ID of the question to check
 * @returns Boolean indicating if a default answer is selected
 */
export const selectIsAnyDefaultAnswerSelectedForQuestion = (
    state: RootState,
    questionId: string
) => {
    const question = selectQuestion(state, questionId);
    return !!question?.defaultAnswer?.id;
};
